<template>
  <v-app>
    <v-navigation-drawer
      v-if="isAuthenticated"
      v-model="drawer"
      app
      clipped
      :permanent="$vuetify.display.lgAndUp"
    >
      <v-list nav>
        <v-list-item
          v-for="item in navigationItems"
          :key="item.title"
          :to="item.to"
          :prepend-icon="item.icon"
          :title="item.title"
        />
      </v-list>
    </v-navigation-drawer>

    <v-app-bar
      v-if="isAuthenticated"
      app
      clipped-left
      color="primary"
      dark
    >
      <v-app-bar-nav-icon
        v-if="$vuetify.display.mdAndDown"
        @click="drawer = !drawer"
      />
      
      <v-toolbar-title>Spectrum Integration Dashboard</v-toolbar-title>
      
      <v-spacer />
      
      <v-btn
        icon="mdi-theme-light-dark"
        @click="toggleTheme"
      />
      
      <v-menu>
        <template #activator="{ props }">
          <v-btn
            icon="mdi-account-circle"
            v-bind="props"
          />
        </template>
        <v-list>
          <v-list-item>
            <v-list-item-title>{{ userDisplayName }}</v-list-item-title>
            <v-list-item-subtitle>{{ userEmail }}</v-list-item-subtitle>
          </v-list-item>
          <v-divider />
          <v-list-item @click="logout">
            <v-list-item-title>
              <v-icon start>mdi-logout</v-icon>
              Sign Out
            </v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </v-app-bar>

    <v-main>
      <router-view />
    </v-main>

    <!-- Multiple stacked snackbars -->
    <div class="snackbar-container">
      <v-snackbar
        v-for="(snack, index) in notificationStore.snackbars"
        :key="snack.id"
        :model-value="snack.show"
        :color="snack.color"
        :timeout="snack.timeout"
        :style="{ bottom: `${index * 60}px` }"
        location="bottom"
        @update:model-value="(val) => !val && notificationStore.removeSnackbar(snack.id)"
      >
        {{ snack.message }}
        <template #actions>
          <v-btn
            variant="text"
            @click="notificationStore.removeSnackbar(snack.id)"
          >
            Close
          </v-btn>
        </template>
      </v-snackbar>
    </div>
  </v-app>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useTheme, useDisplay } from 'vuetify'
import { useAuthStore } from '@/stores/auth'
import { useNotificationStore } from '@/stores/notifications'
import { useRole } from '@/composables/useRole'

const theme = useTheme()
const display = useDisplay()
const authStore = useAuthStore()
const notificationStore = useNotificationStore()
const { isAdmin } = useRole()

const drawer = ref(display.lgAndUp.value)

const isAuthenticated = computed(() => authStore.isAuthenticated)
const userDisplayName = computed(() => authStore.user?.name || 'User')
const userEmail = computed(() => authStore.user?.username || '')

const allNavigationItems = [
  { title: 'Dashboard', icon: 'mdi-view-dashboard', to: '/dashboard' },
  { title: 'Sessions', icon: 'mdi-clock-outline', to: '/sessions' },
  { title: 'Employees', icon: 'mdi-account-group', to: '/employees' },
  { title: 'Mappings', icon: 'mdi-map', to: '/mappings' },
  { title: 'Export', icon: 'mdi-download', to: '/exports' },
  { title: 'Configuration', icon: 'mdi-cog', to: '/configuration', requiresAdmin: true },
  { title: 'User Action Logs', icon: 'mdi-history', to: '/user-action-logs', requiresAdmin: true },
  { title: 'System Errors', icon: 'mdi-alert-circle', to: '/errors' },
  { title: 'Help', icon: 'mdi-help-circle', to: '/help' }
]

const navigationItems = computed(() => {
  return allNavigationItems.filter(item => {
    // If item requires admin role, only show it to admins
    if (item.requiresAdmin) {
      return isAdmin.value
    }
    return true
  })
})

const toggleTheme = () => {
  theme.global.name.value = theme.global.current.value.dark ? 'light' : 'dark'
}

const logout = async () => {
  await authStore.logout()
}

watch(() => display.lgAndUp.value, (newVal) => {
  if (newVal) {
    drawer.value = true
  }
})

</script>

<style>
/* Global styles */
.text-transparent {
  color: transparent !important;
}

.table-headers thead th {
  font-weight: normal !important;
  font-size: 0.875rem !important;
  text-transform: uppercase !important;
  letter-spacing: 0.05em !important;
}

.pre-line {
  white-space: pre-line;
}

.no-line-clamp {
  line-clamp: unset !important;
  -webkit-line-clamp: unset !important;
}

.min-h-32 {
  min-height: 32px;
}

.snackbar-container {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 9999;
  pointer-events: none;
}

.snackbar-container .v-snackbar {
  pointer-events: auto;
}

</style>

<style scoped>
.v-navigation-drawer {
  z-index: 1005;
}
</style>
